import clsx from 'clsx';
import { Column } from 'react-table';
import Header from '../../../../../../modules/components/datatable/Header';
import SimpleDatatable from '../../../../../../modules/components/datatable/SimpleDatatable';
import UnlabeledBulletColumns from '../../../../../../modules/components/list/UnlabeledBulletColumns';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { getSexualCategoryLabel } from '../../../../mappings';
import ToggleLabelCard from '../../card/ToggleLabelCard';

interface Props {
  data: Array<{
    name: string;
    isHidden: boolean;
    imagePaths: string[];
    keywords: Array<{
      original: string;
      language: string;
      english: string;
      isHidden: boolean;
    }>;
    reason: string;
  }>;
  isSiAiNewData: boolean;
  onToggleVisibility: (category: string, keywordIndex: number) => void;
  onExpandImages: (category: string, images: string[]) => void;
  isEditable: boolean;
}

export default function SexualCategoryTable({
  data,
  isSiAiNewData,
  onToggleVisibility,
  onExpandImages,
  isEditable,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_CATEGORY_DETAIL.${id}` }, values);
  const [expandedKeywords, setExpandedKeywords] = useState<Set<string>>(new Set());
  const initialKeywordsNum = 2;
  const emDash = '—';

  const columns: Column<Props['data'][number]>[] = [
    {
      Header: () => <Header>{t('TABLE_CATEGORY')}</Header>,
      accessor: (row) => {
        return (
          <ToggleLabelCard
            label={getSexualCategoryLabel(row.name, intl.formatMessage)}
            thumbnail={row.imagePaths?.[0]}
            onClickThumbnail={() => {
              onExpandImages(row.name, row.imagePaths);
            }}
            isHidden={row?.isHidden}
          />
        );
      },
      minWidth: 80,
      id: 'name',
    },
    {
      Header: () => <Header>{t('TABLE_REASON')}</Header>,
      accessor: (row) => {
        return (
          <>
            {row.reason ?? emDash}
          </>
        );
      },
      minWidth: 200,
      id: 'reason',
    },
    {
      Header: () => (
        <Header>
          {isSiAiNewData ? t('TABLE_ENABLE') : t('TABLE_SOURCE_KEYWORD_TRANSLATED')}
        </Header>
      ),
      accessor: (row) => {
        const showAll = row.keywords.length > 0 && expandedKeywords.has(row.name);
        const keywords = showAll ? row.keywords : row.keywords.slice(0, initialKeywordsNum);

        return (
          <div>
            {row.keywords.length > 0 ? (
              <>
                <UnlabeledBulletColumns
                  columnCount={row.keywords.length > initialKeywordsNum ? 2 : 1}
                  className={{ container: 'list-unstyled m-0', itemContainer: 'mb-2' }}
                  data={keywords.map((keyword, kIndex) => (
                    <ToggleLabelCard
                      key={kIndex}
                      label={
                        keyword.original && (
                          <>
                            {keyword.language && (
                              <span
                                className='badge badge-light badge-sm me-2 align-middle text-uppercase'
                                title={t('LANGUAGE')}
                              >
                                {keyword.language}
                              </span>
                            )}
                            <span>{keyword.original}</span>
                            {keyword.language && keyword.language !== 'en' && keyword.english && (
                              <span> / {keyword.english}</span>
                            )}
                          </>
                        )
                      }
                      isHidden={keyword.isHidden}
                      onToggle={() => {
                        onToggleVisibility(row.name, kIndex);
                      }}
                      showToggle={isEditable}
                    />
                  ))}
                />
                {row.keywords.length > initialKeywordsNum && (
                  <button
                    className='btn btn-link btn-color-muted btn-active-color-primary btn-sm py-1'
                    onClick={(e) => {
                      e.currentTarget.blur();
                      setExpandedKeywords((prev) => {
                        const newSet = new Set(prev);
                        if (showAll) newSet.delete(row.name);
                        else newSet.add(row.name);
                        return newSet;
                      });
                    }}
                  >
                    {showAll ? (
                      <>
                        {t('SHOW_LESS')} <i className='bi bi-chevron-compact-up fs-3'></i>
                      </>
                    ) : (
                      <>
                        {t('SHOW_ALL_MORE', { remaining: row.keywords.length - initialKeywordsNum })}
                        <i className='bi bi-chevron-compact-down fs-3 ms-1' />
                      </>
                    )}
                  </button>
                )}
              </>
            ) : (
              emDash
            )}
          </div>
        );
      },
      minWidth: 200,
      id: 'source',
    },
  ];

  return isEditable ? <SimpleDatatable data={data} columns={columns} className='m-0' /> : <></>;
}
